/**
 * <DischargeActivationTile> — admin dashboard tile (Feb 2026).
 *
 * Renders the hospital-pilot activation metric from
 * `GET /api/admin/readmission/activation-metrics`. This is the leading
 * indicator procurement teams ask about during pilot expansion: "Of the
 * discharge referrals you've placed, what % converted into actual clinical
 * contact (first vitals recorded) inside 48 h?"
 *
 * Sits right above the standard admin stats grid because (a) it crosses
 * multiple tabs (referrals, clinicians, claims) and (b) it's the single
 * number you want to see the moment you log in if a pilot is running.
 */
import React, { useEffect, useState } from 'react';
import axios from '../../lib/api';
import { Activity, BellRing, CheckCircle2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATE_BAND = (rate) => {
  if (rate >= 75) return { cls: 'text-cn-success border-cn-success/40 bg-cn-success/8', label: 'Healthy' };
  if (rate >= 50) return { cls: 'text-cn-accent border-cn-accent/40 bg-cn-accent/8', label: 'Watch' };
  if (rate > 0)   return { cls: 'text-red-600 border-red-500/40 bg-red-500/8', label: 'At risk' };
  return { cls: 'text-cn-text-muted border-cn-border bg-cn-bg/40', label: 'No data' };
};

export const DischargeActivationTile = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/readmission/activation-metrics`, getAuthHeader?.());
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Unable to load activation metrics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-5 mb-6 animate-pulse h-32"
           data-testid="activation-tile-loading" />
    );
  }
  if (err) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-5 mb-6"
           data-testid="activation-tile-error">
        <p className="text-sm font-semibold text-red-700 dark:text-red-300">Activation metrics unavailable</p>
        <p className="text-xs text-red-600 mt-1">{err}</p>
      </div>
    );
  }

  const rate = Number(data?.activation_rate_pct ?? 0);
  const band = RATE_BAND(rate);
  const total = data?.total_eligible ?? 0;
  const withVitals = data?.with_vitals ?? 0;
  const nudges = data?.nudges_sent ?? 0;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-5 sm:p-6 mb-6"
         data-testid="discharge-activation-tile">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Discharge pilot · activation</p>
          <h3 className="text-lg font-display font-extrabold text-slate-900 dark:text-white mt-1">
            Placement → first vitals (48 h window)
          </h3>
          <p className="text-xs text-cn-text-muted mt-1 max-w-xl">
            Of discharge placements that have been on a clinician's roster for at
            least 48 h, what % have a first vitals reading? The leading indicator
            hospital procurement asks about during pilot expansion.
          </p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded border ${band.cls} shrink-0`}
              data-testid="activation-tile-band">
          {band.label}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="px-4 py-3 rounded-xl border border-cn-primary/30 bg-cn-primary/8">
          <div className="flex items-center gap-2 text-cn-primary mb-1">
            <Activity className="w-4 h-4" />
            <p className="text-[10px] uppercase tracking-wider font-bold">Activation rate</p>
          </div>
          <p className="text-3xl font-display font-extrabold text-cn-primary"
             data-testid="activation-tile-rate">{rate.toFixed(1)}%</p>
        </div>

        <div className="px-4 py-3 rounded-xl border border-cn-border bg-cn-bg/40">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 mb-1">
            <Users className="w-4 h-4" />
            <p className="text-[10px] uppercase tracking-wider font-bold">Eligible</p>
          </div>
          <p className="text-2xl font-display font-extrabold text-slate-900 dark:text-white"
             data-testid="activation-tile-eligible">{total}</p>
          <p className="text-[10px] text-cn-text-muted mt-0.5">Placements ≥48 h old</p>
        </div>

        <div className="px-4 py-3 rounded-xl border border-cn-border bg-cn-bg/40">
          <div className="flex items-center gap-2 text-cn-success mb-1">
            <CheckCircle2 className="w-4 h-4" />
            <p className="text-[10px] uppercase tracking-wider font-bold">With vitals</p>
          </div>
          <p className="text-2xl font-display font-extrabold text-slate-900 dark:text-white"
             data-testid="activation-tile-with-vitals">{withVitals}</p>
          <p className="text-[10px] text-cn-text-muted mt-0.5">First reading recorded</p>
        </div>

        <div className="px-4 py-3 rounded-xl border border-cn-border bg-cn-bg/40">
          <div className="flex items-center gap-2 text-cn-accent mb-1">
            <BellRing className="w-4 h-4" />
            <p className="text-[10px] uppercase tracking-wider font-bold">Nudges sent</p>
          </div>
          <p className="text-2xl font-display font-extrabold text-slate-900 dark:text-white"
             data-testid="activation-tile-nudges">{nudges}</p>
          <p className="text-[10px] text-cn-text-muted mt-0.5">Auto first-vitals nudge</p>
        </div>
      </div>
    </div>
  );
};

export default DischargeActivationTile;
